import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ProjectWithStatus, getProjectIcon, getProjectCategory } from '../../types/project.types';
import { useFeatures } from '../../hooks/useFeatures';
import { useAuthStore } from '../../store/authStore';
import { PremiumProjectModal } from './PremiumProjectModal';

const STATUS_LABEL: Record<ProjectWithStatus['status'], { label: string; className: string }> = {
  completed: { label: 'Tamamlandı', className: 'completed' },
  in_progress: { label: 'Devam Ediyor', className: 'pending' },
  not_started: { label: 'Yeni Görev', className: 'pending' },
};

const DIFFICULTY_HOURS: Record<string, number> = {
  beginner: 2,
  intermediate: 3,
  advanced: 4,
};

const DIFFICULTY_XP: Record<string, number> = {
  beginner: 150,
  intermediate: 250,
  advanced: 350,
};

interface ProjectCardProps {
  project: ProjectWithStatus;
}

export function ProjectCard({ project }: ProjectCardProps) {
  const { hasFeature } = useFeatures();
  const { profile } = useAuthStore();
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const status = STATUS_LABEL[project.status];
  const cat = getProjectCategory(project);
  
  const isAdmin = profile?.role === 'admin';
  const canAccessPremium = isAdmin || hasFeature('lesson.premium') || hasFeature('course.premium');
  const isPremiumProject = project.is_premium === true;
  const isLocked = isPremiumProject && !canAccessPremium;

  return (
    <>
      <div 
        className="project-card" 
        style={isLocked ? { opacity: 0.92, cursor: 'pointer', borderColor: 'rgba(99, 102, 241, 0.45)' } : undefined}
        onClick={() => {
          if (isLocked) setShowPremiumModal(true);
        }}
      >
        <div className="project-header">
          <div className="project-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getProjectIcon(project.title)} {project.title}
            {isPremiumProject && (
              <span style={{ fontSize: '0.75rem', background: 'rgba(99,102,241,0.15)', color: 'var(--primary-light)', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>
                Premium
              </span>
            )}
          </div>
          {isLocked ? (
            <span className="project-status pending" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#F87171' }}>Kilitli</span>
          ) : (
            <span className={`project-status ${status.className}`}>{status.label}</span>
          )}
        </div>
        <div className="project-desc">{project.description}</div>
        <div className="project-meta">
          <span>{cat.icon} {cat.label}</span>
          <span>⏱️ {DIFFICULTY_HOURS[project.difficulty] ?? 2} Saat</span>
          <span>⭐ {DIFFICULTY_XP[project.difficulty] ?? 150} XP</span>
          {project.bestScore !== null && !isLocked && <span>🏆 En İyi Skor: %{project.bestScore}</span>}
        </div>
        <div style={{ marginTop: 16 }}>
          {isLocked ? (
            <button
              type="button"
              className="lesson-btn sm primary"
              style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', border: 'none', color: '#fff', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                setShowPremiumModal(true);
              }}
            >
              🔒 Kilidi Aç ve İncele
            </button>
          ) : (
            <Link
              to={`/projects/${project.id}`}
              className={`lesson-btn sm ${project.status === 'not_started' ? 'primary' : 'secondary'}`}
            >
              {project.status === 'not_started' && 'Başla'}
              {project.status === 'in_progress' && 'Devam Et'}
              {project.status === 'completed' && 'Görüntüle'}
            </Link>
          )}
        </div>
      </div>
      <PremiumProjectModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        projectTitle={project.title}
        projectDescription={project.description}
      />
    </>
  );
}

